/**
 * MobileDatePicker Component
 *
 * A mobile-optimized date picker that displays available booking dates as horizontally
 * scrollable pills. Users can navigate between months and select available dates.
 *
 * BOOKING WINDOW CONSTRAINTS:
 * This component enforces booking window constraints from the practice settings:
 * - leadTimeToBookInDays: Minimum days in advance a booking must be made (e.g., 0 = today, 1 = tomorrow)
 * - howFarCanBookInDays: Maximum days in the future a booking can be made (e.g., 49 = ~7 weeks)
 *
 * These constraints:
 * 1. Filter out dates outside the allowed booking window from the available dates list
 * 2. Disable the forward navigation arrow when at the maximum allowed month
 * 3. Prevent month navigation beyond the maximum booking date
 *
 * The values come from the practice API response (currentPractice.leadTimeToBookInDays and
 * currentPractice.howFarCanBookInDays) and are passed as props from Availability.jsx.
 */
import React, { useEffect, useState } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { Spin, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { isEmpty, isNull } from "lodash";
import { isMobile } from "react-device-detect";
import { getAvailabilityRange, getAvailabilityRangeLoadingState } from "./reducers";
import { trackDatePickerInteraction, trackButtonClick } from "utils/analytics";
import {
	fetchAvailabilityBetweenRangeRequest,
} from "./actions";

const format = "YYYY-MM-DD";
const uiFormat = "DD/MM/YYYY";

function MobileDatePicker({
	changeFromDate,
	id,
	fromDate,
	availabilityRange,
	practiceId,
	themeColor,
	leadTimeToBookInDays,
	howFarCanBookInDays,
	onSelectNextDate,
}) {
	const [earliestDate, setEarliestDate] = React.useState(null);
	const [selectedMonth, setSelectedMonth] = React.useState(moment());
  const [selectedDate, setSelectedDate] = React.useState(null);
	const scrollContainerRef = React.useRef(null);
	const scrollCountRef = React.useRef(0);
	const scrollTimeoutRef = React.useRef(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(true);

  const availabilityRangeLoadingState = useSelector(getAvailabilityRangeLoadingState);
	const dispatch = useDispatch();

	const initialiSelectedDate = React.useMemo(() => earliestDate ? earliestDate : null, [earliestDate]);

	// Update scroll arrow visibility
	const updateScrollArrows = React.useCallback(() => {
		if (!scrollContainerRef.current) return;
		const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
		setCanScrollLeft(scrollLeft > 0);
		setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
	}, []);

	// Track scroll interactions on the date picker (debounced)
	const handleScroll = React.useCallback(() => {
		scrollCountRef.current += 1;
		updateScrollArrows();

		// Clear existing timeout
		if (scrollTimeoutRef.current) {
			clearTimeout(scrollTimeoutRef.current);
		}

		// Debounce: track after user stops scrolling for 500ms
		scrollTimeoutRef.current = setTimeout(() => {
			if (scrollCountRef.current > 0) {
				trackDatePickerInteraction('scroll', {
					scroll_count: scrollCountRef.current,
					month_viewed: selectedMonth.format('MMMM YYYY'),
				});
				scrollCountRef.current = 0;
			}
		}, 500);
	}, [selectedMonth, updateScrollArrows]);

	// Scroll by arrow click (desktop)
	const handleArrowScroll = (direction) => {
		if (!scrollContainerRef.current) return;
		const scrollAmount = 280; // Scroll ~4 pills at a time
		const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction * scrollAmount);
		scrollContainerRef.current.scrollTo({
			left: newScrollLeft,
			behavior: 'smooth',
		});
		// Update arrows after animation
		setTimeout(updateScrollArrows, 300);
	};

	/**
	 * Calculate the booking window boundaries based on practice settings.
	 *
	 * minBookingDate: The earliest date a user can book (today + leadTimeToBookInDays)
	 * maxBookingDate: The latest date a user can book (today + howFarCanBookInDays)
	 *
	 * These are used to:
	 * - Filter which dates appear as selectable pills
	 * - Control whether the forward/back month navigation arrows are enabled
	 */
	const minBookingDate = React.useMemo(() => {
		if (leadTimeToBookInDays !== undefined && leadTimeToBookInDays !== null) {
			return moment().add(leadTimeToBookInDays, 'days');
		}
		return moment(); // Default to today if not set
	}, [leadTimeToBookInDays]);

	const maxBookingDate = React.useMemo(() => {
		if (howFarCanBookInDays !== undefined && howFarCanBookInDays !== null && howFarCanBookInDays > 0) {
			return moment().add(howFarCanBookInDays, 'days');
		}
		return moment().add(2, 'years'); // Default to 2 years if not set
	}, [howFarCanBookInDays]);

	useEffect(() => {
		// Fetch availability for current month
		const extraDaysBefore = selectedMonth.clone().startOf('month').weekday();
		const extraDaysAfter = (7 - selectedMonth.clone().endOf('month').weekday()) + 7;

		dispatch(fetchAvailabilityBetweenRangeRequest(
			practiceId,
			selectedMonth.clone().startOf('month').subtract(extraDaysBefore, "days").format(uiFormat),
			selectedMonth.clone().endOf('month').add(extraDaysAfter, "days").format(uiFormat),
		));
	}, [dispatch, practiceId, selectedMonth]);

	/**
	 * Get available dates for the currently selected month.
	 *
	 * Filtering pipeline:
	 * 1. Only include dates marked as available from the API
	 * 2. Only include dates within the selected month/year
	 * 3. Only include dates within the booking window (minBookingDate to maxBookingDate)
	 * 4. Remove duplicates and sort chronologically
	 */
	const availableDates = React.useMemo(() => {
		if (!availabilityRange || isEmpty(availabilityRange)) return [];

		const dates = availabilityRange.flat()
			.filter(d => d.available)
			.map(d => moment(d.day, format))
			.filter(d => d.month() === selectedMonth.month() && d.year() === selectedMonth.year())
			// Filter by booking window constraints - only show dates within allowed range
			.filter(d => !d.isBefore(minBookingDate, 'day') && !d.isAfter(maxBookingDate, 'day'))
			.sort((a, b) => a.valueOf() - b.valueOf());

		// Remove duplicates
		const uniqueDates = [];
		dates.forEach(d => {
			if (!uniqueDates.find(ud => ud.isSame(d, 'day'))) {
				uniqueDates.push(d);
			}
		});

		return uniqueDates;
	}, [availabilityRange, selectedMonth, minBookingDate, maxBookingDate]);

	const earliestAvailability = React.useCallback(() => {
		if (isEmpty(availabilityRange)) return;

		// Filter available dates by booking window constraints
		const availableDatesList = availabilityRange.flat()
			.filter(d => d.available)
			.map(d => moment(d.day, format))
			.filter(d => !d.isBefore(minBookingDate, 'day') && !d.isAfter(maxBookingDate, 'day'))
			.sort((a, b) => a.valueOf() - b.valueOf());

		if (availableDatesList.length > 0) {
			const date = availableDatesList[0];
			setEarliestDate(date);
			setSelectedMonth(date.clone());
      setSelectedDate(date);
			changeFromDate(practiceId, date.format(uiFormat));
		}
	}, [availabilityRange, minBookingDate, maxBookingDate, changeFromDate, practiceId]);

	const handleDateSelect = React.useCallback((date) => {
		// Track date selection
		trackDatePickerInteraction('date_tap', {
			selected_date: date.format('YYYY-MM-DD'),
			day_of_week: date.format('dddd'),
			days_from_today: date.diff(moment(), 'days'),
		});
		trackButtonClick('date_pill', { date: date.format('YYYY-MM-DD') });

		changeFromDate(id, date.format(uiFormat));
    setSelectedDate(date);
	}, [changeFromDate, id]);

	/**
	 * Select the next available date after the current one.
	 * This is called by the parent component when the current date has no time slots.
	 */
	// const selectNextAvailableDate = React.useCallback(() => {
	// 	if (!availableDates || availableDates.length === 0) return;

	// 	const currentDate = fromDate ? moment(fromDate, uiFormat) : null;
	// 	if (!currentDate) return;

	// 	// Find the next date after the current one
	// 	const nextDate = availableDates.find(d => d.isAfter(currentDate, 'day'));
	// 	if (nextDate) {
	// 		changeFromDate(id, nextDate.format(uiFormat));
	// 	}
	// }, [availableDates, fromDate, changeFromDate, id]);

	// Expose the selectNextAvailableDate function to parent
	// useEffect(() => {
	// 	if (onSelectNextDate) {
	// 		onSelectNextDate(selectNextAvailableDate);
	// 	}
	// }, [selectNextAvailableDate, onSelectNextDate]);

	useEffect(() => {
		if (isNull(earliestDate)) {
			earliestAvailability();
		}
	}, [availabilityRange, earliestAvailability, earliestDate]);

	/**
	 * Handle month navigation (forward/back arrows).
	 * Enforces booking window constraints:
	 * - Cannot go before the current month
	 * - Cannot go past the month containing maxBookingDate
	 */
  const handleMonthChange = (direction) => {
    const newMonth = selectedMonth.clone().add(direction, 'month');
    if (newMonth.isBefore(moment(), 'month')) return;
    if (newMonth.clone().startOf('month').isAfter(maxBookingDate, 'month')) return;

    trackDatePickerInteraction('month_change', {
        direction: direction > 0 ? 'forward' : 'backward',
        from_month: selectedMonth.format('MMMM YYYY'),
        to_month: newMonth.format('MMMM YYYY'),
    });
    trackButtonClick(direction > 0 ? 'date_picker_next_month' : 'date_picker_prev_month');

    setSelectedMonth(newMonth);

    // Auto-select the earliest available date in the new month
    if (availabilityRange && !isEmpty(availabilityRange)) {
        const newMonthDates = availabilityRange.flat()
            .filter(d => d.available)
            .map(d => moment(d.day, format))
            .filter(d => d.month() === newMonth.month() && d.year() === newMonth.year())
            .filter(d => !d.isBefore(minBookingDate, 'day') && !d.isAfter(maxBookingDate, 'day'))
            .sort((a, b) => a.valueOf() - b.valueOf());

        if (newMonthDates.length > 0) {
            const earliest = newMonthDates[0];
            setSelectedDate(earliest);
            changeFromDate(id, earliest.format(uiFormat));
        } else {
            setSelectedDate(null);
        }
    }
  };

	const isToday = (date) => date.isSame(moment(), 'day');
	const isTomorrow = (date) => date.isSame(moment().add(1, 'day'), 'day');
	const isSelected = (date) => selectedDate && date.isSame(selectedDate, 'day');

	const getDateLabel = (date) => {
		if (isToday(date)) return 'Today';
		if (isTomorrow(date)) return 'Tomorrow';
		return date.format('ddd');
	};

	// Scroll to selected date on mount and update arrows
	useEffect(() => {
		if (scrollContainerRef.current && initialiSelectedDate) {
			const selectedIndex = availableDates.findIndex(d => d.isSame(initialiSelectedDate, 'day'));
			if (selectedIndex > 0) {
				const scrollAmount = selectedIndex * 72; // approximate width of each pill
				scrollContainerRef.current.scrollLeft = Math.max(0, scrollAmount - 50);
			}
			// Update arrow state after scroll
			setTimeout(updateScrollArrows, 100);
		}
	}, [availableDates, initialiSelectedDate, updateScrollArrows]);

	// Update arrows when dates change
	useEffect(() => {
		updateScrollArrows();
	}, [availableDates, updateScrollArrows]);

	// Navigation arrow state - controls whether arrows are clickable/visible
	const isPastMonth = selectedMonth.isSameOrBefore(moment(), 'month');
	// Disable forward arrow if we're already at or past the month containing maxBookingDate
	const isAtMaxMonth = selectedMonth.isSameOrAfter(maxBookingDate, 'month');

	return (
		<div>
			{/* Month selector */}
			<div style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				marginBottom: 12,
			}}>
				<div
					onClick={() => !isPastMonth && handleMonthChange(-1)}
					style={{
						padding: 8,
						cursor: isPastMonth ? 'not-allowed' : 'pointer',
						opacity: isPastMonth ? 0.3 : 1,
					}}
				>
					<LeftOutlined />
				</div>
				<Typography.Text strong style={{ fontSize: 18 }}>
					{selectedMonth.format('MMMM YYYY')}
				</Typography.Text>
				<div
					onClick={() => !isAtMaxMonth && handleMonthChange(1)}
					style={{
						padding: 8,
						cursor: isAtMaxMonth ? 'not-allowed' : 'pointer',
						opacity: isAtMaxMonth ? 0.3 : 1,
					}}
				>
					<RightOutlined />
				</div>
			</div>

			{/* Available dates as horizontal scrollable pills */}
			{availabilityRangeLoadingState ? (
				<div style={{ textAlign: 'center', padding: 20 }}>
					<Spin size="small" />
				</div>
			) : availableDates.length === 0 ? (
				<Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '16px 0' }}>
					No available dates this month
				</Typography.Text>
			) : (
				<div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
					{/* Left scroll arrow - desktop only */}
					{!isMobile && (
						<div
							onClick={() => canScrollLeft && handleArrowScroll(-1)}
							style={{
								position: 'absolute',
								left: 0,
								zIndex: 2,
								width: 32,
								height: 32,
								borderRadius: '50%',
								background: canScrollLeft ? '#fff' : '#f5f5f5',
								boxShadow: canScrollLeft ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: canScrollLeft ? 'pointer' : 'not-allowed',
								opacity: canScrollLeft ? 1 : 0.4,
								transition: 'all 0.2s',
							}}
						>
							<LeftOutlined style={{ fontSize: 12, color: canScrollLeft ? '#333' : '#999' }} />
						</div>
					)}

					<div
						ref={scrollContainerRef}
						onScroll={handleScroll}
						style={{
							display: 'flex',
							gap: 8,
							overflowX: 'auto',
							paddingBottom: 8,
							paddingLeft: !isMobile ? 40 : 0,
							paddingRight: !isMobile ? 40 : 0,
							WebkitOverflowScrolling: 'touch',
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							flex: 1,
						}}
					>
						<style>{`
						.mobile-date-scroll::-webkit-scrollbar {
							display: none;
						}
					`}</style>
						{availableDates.map((date, index) => {
							const selected = isSelected(date);
							return (
								<div
									key={index}
									onClick={() => handleDateSelect(date)}
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										padding: '10px 12px',
										minWidth: 64,
										borderRadius: 12,
										cursor: 'pointer',
										background: selected ? (themeColor || '#000') : '#f5f5f5',
										color: selected ? '#fff' : '#333',
										transition: 'all 0.2s',
										flexShrink: 0,
									}}
								>
									<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
										{getDateLabel(date)}
									</span>
									<span style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2 }}>
										{date.format('D')}
									</span>
								</div>
							);
						})}
					</div>

					{/* Right scroll arrow - desktop only */}
					{!isMobile && (
						<div
							onClick={() => canScrollRight && handleArrowScroll(1)}
							style={{
								position: 'absolute',
								right: 0,
								zIndex: 2,
								width: 32,
								height: 32,
								borderRadius: '50%',
								background: canScrollRight ? '#fff' : '#f5f5f5',
								boxShadow: canScrollRight ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: canScrollRight ? 'pointer' : 'not-allowed',
								opacity: canScrollRight ? 1 : 0.4,
								transition: 'all 0.2s',
							}}
						>
							<RightOutlined style={{ fontSize: 12, color: canScrollRight ? '#333' : '#999' }} />
						</div>
					)}
				</div>
			)}
		</div>
	);
}

const mapStateToProps = (state) => ({
	availabilityRange: getAvailabilityRange(state.availability),
});

export default connect(mapStateToProps, {})(MobileDatePicker);
